import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { getIconName } from '../utils/iconMatcher'

export const useProjectStore = defineStore('projects', () => {
  const projects = ref([])
  const loading = ref(false)
  const error = ref(null)
  const activeFilter = ref('All')
  
  // NOUVEAU : Cache en mémoire vive pour l'instantannéité
  const readmeCache = ref({}) 

  const GITHUB_USERNAME = 'Quentin-BLDJ'
  const CACHE_KEY = 'portfolio_projects_v6'
  const CACHE_DURATION = 60 * 15 * 1000 

  // --- PARSEUR DE DESCRIPTION ---
  const parseDescription = (desc) => {
    if (!desc) return { fr: "Pas de description.", en: "No description available." }
    const regex = /FR\s*:\s*(.*?)\s*(?:EN\s*:\s*(.*))?$/i
    const match = desc.match(regex)
    if (match) {
      return {
        fr: match[1] || desc,
        en: match[2] || match[1] || desc
      }
    }
    return { fr: desc, en: desc }
  }

  const processData = (data) => {
    if (!Array.isArray(data)) return [] 
    return data.filter(repo => {
      return !repo.fork && repo.description 
    }).map(repo => {
      const allTechs = []
      if (repo.language) allTechs.push(repo.language)
      if (repo.topics) allTechs.push(...repo.topics)
      const uniqueTechs = [...new Set(allTechs.map(t => t.toLowerCase()))]

      return {
        id: repo.id,
        name: repo.name,
        description: parseDescription(repo.description),
        html_url: repo.html_url,
        homepage: repo.homepage,
        default_branch: repo.default_branch,
        technologies: uniqueTechs.map(tech => ({
          name: tech, 
          iconKey: getIconName(tech)
        })),
        rawTags: uniqueTechs
      }
    })
  }

  const fetchProjects = async (forceRefresh = false) => {
    if (loading.value) return 

    if (!forceRefresh && isCacheValid()) {
      const { data } = JSON.parse(localStorage.getItem(CACHE_KEY))
      projects.value = data
      return 
    }

    loading.value = true 
    error.value = null

    try {
      const response = await fetch(`https://api.github.com/users/${GITHUB_USERNAME}/repos?sort=updated&per_page=100`)
      if (!response.ok) throw new Error(`GitHub Error: ${response.status}`)
      
      const rawData = await response.json()
      const processed = processData(rawData)
      projects.value = processed
      
      localStorage.setItem(CACHE_KEY, JSON.stringify({
        timestamp: Date.now(),
        data: processed
      }))
    } catch (err) {
      console.warn("Erreur fetch, fallback...", err)
      error.value = "Impossible de mettre à jour les projets."
      if (localStorage.getItem(CACHE_KEY)) {
         projects.value = JSON.parse(localStorage.getItem(CACHE_KEY)).data
      }
    } finally {
      loading.value = false
    }
  }

  // --- NOUVELLE LOGIQUE FETCH README (Optimisée) ---
  const fetchReadme = async (repoName, defaultBranch, lang = 'en') => {
    // 1. Générer une clé unique
    const cacheKey = `${repoName}-${lang}`

    // 2. Si c'est déjà en RAM, on retourne direct (0 latence)
    if (readmeCache.value[cacheKey]) {
      return readmeCache.value[cacheKey]
    }

    // Note : J'ai retiré le timestamp pour permettre au navigateur de cacher aussi
    try {
      let content = ""

      // 3. Tenter le fichier spécifique à la langue (ex: README.fr.md)
      if (lang === 'fr') {
        const resFr = await fetch(`https://raw.githubusercontent.com/${GITHUB_USERNAME}/${repoName}/${defaultBranch}/README.fr.md`)
        if (resFr.ok) {
          content = await resFr.text()
        }
      }
      
      // 4. Fallback sur le README standard si le spécifique échoue
      if (!content) {
        const res = await fetch(`https://raw.githubusercontent.com/${GITHUB_USERNAME}/${repoName}/${defaultBranch}/README.md`)
        content = res.ok ? await res.text() : "Documentation indisponible / No documentation available."
      }
      
      // 5. Sauvegarder dans le store pour la prochaine fois
      readmeCache.value[cacheKey] = content
      return content

    } catch (e) { 
      console.error(e)
      return "Erreur lors du chargement / Loading error." 
    }
  }

  // --- HELPERS & GETTERS ---
  const isCacheValid = () => {
    const cached = localStorage.getItem(CACHE_KEY)
    if (!cached) return false
    try {
      const { timestamp } = JSON.parse(cached)
      return Date.now() - timestamp < CACHE_DURATION
    } catch (e) { return false }
  }
  
  const setFilter = (filter) => { activeFilter.value = filter }
  
  const allTechnologies = computed(() => {
    if (!projects.value) return []
    const techSet = new Set()
    projects.value.forEach(p => {
      p.technologies.forEach(t => techSet.add(t.name))
    })
    return Array.from(techSet).sort()
  })

  const filteredProjects = computed(() => {
    if (!projects.value) return []
    if (activeFilter.value === 'All') return projects.value
    return projects.value.filter(p => {
      const search = activeFilter.value.toLowerCase()
      return p.rawTags.includes(search)
    })
  })

  return { 
    projects, loading, error, activeFilter, 
    allTechnologies, filteredProjects, 
    fetchProjects, fetchReadme, setFilter 
  }
})
